import type { ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { Checkbox } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { useToggleFlash } from "./useToggleFlash";

type Props = {
  label: string;
  sublabel?: string;
  /** "indeterminate" = alguns itens do grupo ativos, outros não (ex: checkbox de uma escala inteira). */
  enabled?: boolean | "indeterminate";
  /** Item que o próprio laboratório criou, em vez de ter vindo do catálogo padrão da plataforma —
   *  ganha um ponto discreto. Todo item da lista é do lab; isto só conta a procedência. */
  proprio?: boolean;
  onToggle?: (enabled: boolean) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
  /** Controle próprio da linha, antes do botão de excluir (ex: contador de fichas). */
  extra?: ReactNode;
  /** Linha que encabeça um grupo (a categoria): nome em negrito e maior. */
  destaque?: boolean;
};

/**
 * Linha de item do catálogo do laboratório.
 *
 * Clicar na linha edita — não há mais lápis. Checkbox, lixeira e os controles de `extra` são
 * cliques com destino próprio e por isso param a propagação; sem isso, marcar o checkbox abriria
 * o diálogo junto. O nome é um `button` de verdade para o teclado chegar nele por Tab.
 *
 * Ao marcar/desmarcar, a linha destaca em verde/vermelho por um instante pra confirmar a mudança.
 */
export function CatalogItemRow({ label, sublabel, enabled, proprio, onToggle, onEdit, onDelete, disabled, extra, destaque }: Props) {
  const { flash, handleToggle } = useToggleFlash(onToggle);
  const clicavel = !!onEdit && !disabled;

  const conteudo = (
    <>
      {/* No celular o nome quebra em vez de ser cortado: "Coroa anatômica sobre Implante" virava
          "Coroa anatômica sobre Impl…", e nome de serviço é o que identifica a linha. No desktop
          segue truncando, pra manter as linhas na mesma altura em listas longas. */}
      <p
        title={label}
        className={cn(
          "flex items-center gap-1.5",
          // O nome da categoria quebra linha: cortado ("Restaurações indiretas e odontolog…") não se lê.
          destaque ? "text-[15px] font-semibold leading-snug text-zinc-900 dark:text-foreground" : "text-sm text-zinc-800 md:truncate dark:text-foreground/90",
        )}
      >
        {label}
        {proprio && (
          <span
            title="Criado por você"
            aria-label="Criado por você"
            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500 dark:bg-primary"
          />
        )}
      </p>
      {sublabel && <p className="truncate text-[11px] text-zinc-400 dark:text-muted-foreground">{sublabel}</p>}
    </>
  );

  return (
    <div
      onClick={clicavel ? onEdit : undefined}
      className={cn(
        "flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors duration-500",
        flash === "green" && "bg-emerald-50 dark:bg-emerald-950/30",
        flash === "red" && "bg-red-50 dark:bg-red-950/30",
        !flash && "hover:bg-zinc-50 dark:hover:bg-white/5",
        clicavel && "cursor-pointer",
      )}
    >
      {onToggle && (
        <span onClick={(e) => e.stopPropagation()} className="flex shrink-0 items-center">
          <Checkbox checked={enabled} disabled={disabled} onCheckedChange={handleToggle} aria-label={`Ativar/desativar ${label}`} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        {onEdit ? (
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            aria-label={`Editar ${label}`}
            className="block w-full min-w-0 text-left disabled:opacity-50"
          >
            {conteudo}
          </button>
        ) : (
          conteudo
        )}
      </div>
      {extra && (
        <span onClick={(e) => e.stopPropagation()} className="flex shrink-0 items-center">
          {extra}
        </span>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={disabled}
          className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/30 dark:hover:text-red-400"
          aria-label={`Excluir ${label}`}
          title="Excluir"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
